import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Building2, Users, FileText, MapPin, CheckCircle, XCircle, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';

const PlatformTenants = () => {
  const { apiFetch } = usePlatformAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');

  const fetchTenants = async () => {
    try {
      let params = '';
      if (filterStatus !== 'all') params += `?status=${filterStatus}`;
      if (filterPlan !== 'all') params += `${params ? '&' : '?'}plan=${filterPlan}`;
      const res = await apiFetch(`/tenants${params}`);
      setTenants(res.data.tenants);
    } catch (err) {
      toast.error('Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTenants(); }, [filterStatus, filterPlan]);

  const updateTenant = async (tenantId, updates) => {
    try {
      await apiFetch(`/tenants/${tenantId}`, { method: 'PATCH', body: JSON.stringify(updates) });
      toast.success('School updated');
      fetchTenants();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const filtered = tenants.filter(t =>
    !search || t.name?.toLowerCase().includes(search.toLowerCase()) || t.state?.toLowerCase().includes(search.toLowerCase()) || t.school_code?.toLowerCase().includes(search.toLowerCase())
  );

  const planColors = { trial: 'bg-slate-600', starter: 'bg-blue-600', professional: 'bg-indigo-600', enterprise: 'bg-amber-600' };
  const statusColors = { active: 'text-emerald-400', inactive: 'text-red-400', suspended: 'text-amber-400' };

  return (
    <div data-testid="platform-tenants">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">School Management</h1>
          <p className="text-slate-400">{tenants.length} registered school(s)</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            placeholder="Search by name or state..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
            data-testid="tenant-search"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-white" data-testid="tenant-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPlan} onValueChange={setFilterPlan}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-white" data-testid="tenant-plan-filter">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" /></div>
      ) : filtered.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800"><CardContent className="p-12 text-center text-slate-500">No schools found</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {filtered.map((tenant) => (
            <Card key={tenant.tenant_id} className="bg-slate-900 border-slate-800" data-testid={`tenant-${tenant.tenant_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Building2 className="h-5 w-5 text-indigo-400" />
                      <h3 className="text-lg font-semibold text-white">{tenant.name}</h3>
                      {tenant.school_code && (
                        <span className="text-xs font-mono bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded-md tracking-wider" data-testid={`tenant-code-${tenant.tenant_id}`}>
                          {tenant.school_code}
                        </span>
                      )}
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white ${planColors[tenant.subscription_tier] || 'bg-slate-600'}`}>
                        {tenant.subscription_tier?.toUpperCase()}
                      </span>
                      <span className={`text-xs font-medium ${statusColors[tenant.status] || 'text-slate-400'}`}>
                        {tenant.status === 'active' ? <CheckCircle className="h-4 w-4 inline mr-1" /> : <XCircle className="h-4 w-4 inline mr-1" />}
                        {tenant.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                      {tenant.state && (
                        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{tenant.state}{tenant.district ? `, ${tenant.district}` : ''}</span>
                      )}
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{tenant.user_count} users (max {tenant.max_users})</span>
                      <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" />{tenant.leave_count} leave requests</span>
                      {tenant.school_board && <span className="uppercase text-xs bg-slate-800 px-2 py-0.5 rounded">{tenant.school_board}</span>}
                    </div>
                    <p className="text-xs text-slate-600 mt-1">ID: {tenant.tenant_id} | Created: {new Date(tenant.created_at).toLocaleDateString('en-IN')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      value={tenant.subscription_tier}
                      onValueChange={(val) => updateTenant(tenant.tenant_id, {
                        subscription_tier: val,
                        max_users: { trial: 10, starter: 50, professional: 500, enterprise: 10000 }[val]
                      })}
                    >
                      <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white text-xs" data-testid={`tenant-plan-${tenant.tenant_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trial">Trial</SelectItem>
                        <SelectItem value="starter">Starter</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                    {tenant.status === 'active' ? (
                      <Button variant="outline" size="sm" className="border-red-800 text-red-400 hover:bg-red-950" onClick={() => updateTenant(tenant.tenant_id, { status: 'inactive' })} data-testid={`deactivate-${tenant.tenant_id}`}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="border-emerald-800 text-emerald-400 hover:bg-emerald-950" onClick={() => updateTenant(tenant.tenant_id, { status: 'active' })} data-testid={`activate-${tenant.tenant_id}`}>
                        Activate
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlatformTenants;
